/**
 * Theme wishlist — localStorage backed.
 * Stores product handles + display meta; syncs [data-wishlist-toggle] buttons and wishlist page.
 */
(function () {
  var STORAGE_KEY = 'theswaf_wishlist_v1';
  var CHANGE_EVENT = 'theswaf:wishlist-change';

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: list } }));
  }

  function normalizeHandle(handle) {
    return String(handle || '')
      .trim()
      .toLowerCase();
  }

  function findIndex(list, handle) {
    var h = normalizeHandle(handle);
    return list.findIndex(function (item) {
      return normalizeHandle(item.handle) === h;
    });
  }

  function isInWishlist(handle) {
    return findIndex(read(), handle) !== -1;
  }

  function getItems() {
    return read();
  }

  function upsertMeta(btn) {
    return {
      handle: normalizeHandle(btn.getAttribute('data-product-handle')),
      id: btn.getAttribute('data-product-id') || '',
      title: btn.getAttribute('data-product-title') || '',
      url: btn.getAttribute('data-product-url') || '',
      image: btn.getAttribute('data-product-image') || '',
      price: btn.getAttribute('data-product-price') || '',
      addedAt: Date.now(),
    };
  }

  function add(item) {
    if (!item || !item.handle) return false;
    var list = read();
    if (findIndex(list, item.handle) !== -1) return false;
    list.unshift(item);
    write(list);
    return true;
  }

  function remove(handle) {
    var list = read();
    var idx = findIndex(list, handle);
    if (idx === -1) return false;
    list.splice(idx, 1);
    write(list);
    return true;
  }

  function toggle(item) {
    if (!item || !item.handle) return false;
    if (isInWishlist(item.handle)) {
      remove(item.handle);
      return false;
    }
    add(item);
    return true;
  }

  function syncButtons(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      var active = isInWishlist(handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function syncHeaderCount() {
    var count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count < 1;
      el.classList.toggle('is-empty', count < 1);
    });
  }

  function onClick(event) {
    var btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();

    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    var active = toggle(upsertMeta(btn));
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function moneyFromCents(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      }).format(Number(cents) / 100);
    } catch (e) {
      return (Number(cents) / 100).toFixed(2);
    }
  }

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('missing');
        return res.json();
      });
  }

  function buildCardHtml(product, item, compact) {
    var variant = product.variants && product.variants[0];
    var image =
      (product.featured_image && (product.featured_image.src || product.featured_image)) ||
      (product.images && product.images[0]) ||
      (item && item.image) ||
      '';
    if (image && image.indexOf('//') === 0) image = 'https:' + image;

    var priceHtml = '';
    if (variant) {
      priceHtml =
        '<div class="wishlist-card__price">' +
        moneyFromCents(variant.price, (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD') +
        '</div>';
    }

    return (
      '<a class="wishlist-card__media" href="' +
      product.url +
      '">' +
      (image
        ? '<img src="' +
          image +
          '" alt="' +
          (product.title || '').replace(/"/g, '&quot;') +
          '" loading="lazy" width="400" height="400">'
        : '') +
      '</a>' +
      '<div class="wishlist-card__body">' +
      '<h3 class="wishlist-card__title"><a href="' +
      product.url +
      '">' +
      (product.title || '') +
      '</a></h3>' +
      priceHtml +
      '<div class="wishlist-card__actions">' +
      (compact
        ? ''
        : '<a class="wishlist-card__view button button--secondary" href="' + product.url + '">View product</a>') +
      '<button type="button" class="wishlist-card__remove" data-wishlist-remove="' +
      product.handle +
      '">Remove</button>' +
      '</div>' +
      '</div>'
    );
  }

  function renderListInto(gridSelector, emptySelector, loadingSelector, compact) {
    var grid = document.querySelector(gridSelector);
    var empty = document.querySelector(emptySelector);
    var loading = document.querySelector(loadingSelector);
    if (!grid) return Promise.resolve();

    var items = read();
    if (loading) loading.hidden = false;
    if (empty) empty.hidden = true;
    grid.innerHTML = '';

    if (!items.length) {
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = false;
      return Promise.resolve();
    }

    return Promise.all(
      items.map(function (item) {
        return fetchProduct(item.handle)
          .then(function (product) {
            return { ok: true, item: item, product: product };
          })
          .catch(function () {
            return { ok: false, item: item };
          });
      })
    ).then(function (results) {
      if (loading) loading.hidden = true;

      var valid = results.filter(function (r) {
        return r.ok;
      });
      var stale = results.filter(function (r) {
        return !r.ok;
      });

      if (stale.length) {
        stale.forEach(function (r) {
          remove(r.item.handle);
        });
      }

      if (!valid.length) {
        if (empty) empty.hidden = false;
        return;
      }

      if (empty) empty.hidden = true;

      valid.forEach(function (r) {
        var li = document.createElement('li');
        li.className = 'wishlist-card';
        li.setAttribute('data-wishlist-handle', r.product.handle);
        li.innerHTML = buildCardHtml(r.product, r.item, compact);
        grid.appendChild(li);
      });
    });
  }

  function renderWishlistPage() {
    return renderListInto('[data-wishlist-grid]', '[data-wishlist-empty]', '[data-wishlist-loading]', false);
  }

  function renderWishlistDrawer() {
    return renderListInto(
      '[data-wishlist-drawer-grid]',
      '[data-wishlist-drawer-empty]',
      '[data-wishlist-drawer-loading]',
      true
    );
  }

  function openDrawer() {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) return;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wishlist-drawer-open');
    renderWishlistDrawer();
  }

  function closeDrawer() {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) return;
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wishlist-drawer-open');
  }

  function onRemoveClick(event) {
    var btn = event.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    event.preventDefault();
    var handle = btn.getAttribute('data-wishlist-remove');
    remove(handle);
    document.querySelectorAll('.wishlist-card[data-wishlist-handle="' + handle + '"]').forEach(function (card) {
      card.remove();
    });
    var pageGrid = document.querySelector('[data-wishlist-grid]');
    var pageEmpty = document.querySelector('[data-wishlist-empty]');
    if (pageGrid && !pageGrid.children.length && pageEmpty) pageEmpty.hidden = false;
    var drawerGrid = document.querySelector('[data-wishlist-drawer-grid]');
    var drawerEmpty = document.querySelector('[data-wishlist-drawer-empty]');
    if (drawerGrid && !drawerGrid.children.length && drawerEmpty) drawerEmpty.hidden = false;
  }

  function onHeaderClick(event) {
    var link = event.target.closest('[data-wishlist-open-drawer]');
    if (!link) return;
    event.preventDefault();
    openDrawer();
  }

  function onDrawerCloseClick(event) {
    if (!event.target.closest('[data-wishlist-drawer-close]')) return;
    closeDrawer();
  }

  function onKeydown(event) {
    if (event.key === 'Escape') closeDrawer();
  }

  function init() {
    syncButtons();
    syncHeaderCount();
    renderWishlistPage();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('click', onRemoveClick);
  document.addEventListener('click', onHeaderClick);
  document.addEventListener('click', onDrawerCloseClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener(CHANGE_EVENT, function () {
    syncButtons();
    syncHeaderCount();
    var drawer = document.querySelector('[data-wishlist-drawer]');
    if (drawer && !drawer.hidden) renderWishlistDrawer();
    if (document.querySelector('[data-wishlist-page]')) renderWishlistPage();
  });
  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();

  window.TheSwafWishlist = {
    getItems: getItems,
    isInWishlist: isInWishlist,
    add: add,
    remove: remove,
    toggle: toggle,
    syncButtons: syncButtons,
    renderWishlistPage: renderWishlistPage,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
  };
})();
